import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { formatDate } from "@/lib/i18n";

/**
 * D3: Sub-Account Management
 * Create, suspend, reactivate, archive. Bulk operations.
 */

interface SubAccount {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

interface AgencyTemplate {
  id: string;
  name: string;
  vertical: string;
  description: string | null;
}

const STATUS_ACTIONS: Record<string, Array<{ label: string; status: string; color: string }>> = {
  active: [
    { label: "Suspend", status: "suspended", color: "text-[var(--color-warning)]" },
    { label: "Archive", status: "archived", color: "text-[var(--color-destructive)]" },
  ],
  suspended: [
    { label: "Reactivate", status: "active", color: "text-[var(--color-success)]" },
    { label: "Archive", status: "archived", color: "text-[var(--color-destructive)]" },
  ],
  archived: [
    { label: "Reactivate", status: "active", color: "text-[var(--color-success)]" },
  ],
};

export default function SubAccountManagementPage() {
  const [accounts, setAccounts] = useState<SubAccount[]>([]);
  const [templates, setTemplates] = useState<AgencyTemplate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [orgId, setOrgId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchAccounts = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<{ data: SubAccount[] }>(`/platform/agency/sub-accounts/${id}`);
      setAccounts(data.data);
      setError(null);
    } catch {
      setAccounts([]);
      setError("Sub-account data is currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<{ data: AgencyTemplate[] }>(`/platform/agency/templates/${id}`);
      setTemplates(data.data);
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("orgId") ?? "";
    setOrgId(id);
    if (id) {
      fetchAccounts(id);
      fetchTemplates(id);
    }
    else {
      setIsLoading(false);
      setError(null);
    }
  }, [fetchAccounts, fetchTemplates]);

  const handleStatusChange = async (tenantId: string, status: string) => {
    try {
      await apiFetch(`/platform/agency/sub-accounts/${tenantId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      setSuccess(`Client status updated to ${status}.`);
      setError(null);
      if (orgId) fetchAccounts(orgId);
    } catch {
      setError("Failed to update client status.");
    }
  };

  const handleBulkAction = async (status: string) => {
    if (selected.size === 0) return;
    try {
      await apiFetch("/platform/agency/sub-accounts/bulk-status", {
        method: "POST",
        body: JSON.stringify({ tenantIds: Array.from(selected), status }),
      });
      setSelected(new Set());
      setSuccess(`${status} applied to ${selected.size} selected clients.`);
      setError(null);
      if (orgId) fetchAccounts(orgId);
    } catch {
      setError("Failed to apply the bulk client update.");
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === accounts.length) setSelected(new Set());
    else setSelected(new Set(accounts.map((a) => a.id)));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        {[1, 2].map((i) => (
          <SkeletonCard key={i} className="h-12" />
        ))}
      </div>
    );
  }

  if (!orgId) {
    return (
      <EmptyState
        title="Organization required"
        description="Open this client-management page with an `orgId` query parameter so the mounted agency client surfaces can be loaded."
      />
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Sub-Accounts ({accounts.length})</h1>
        <button onClick={() => setShowCreate(true)} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          + Create Account
        </button>
      </div>

      {success ? (
        <div className="mb-4 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-2 text-sm text-[var(--color-success)]">
          {success}
        </div>
      ) : null}
      {error ? (
        <div className="mb-4 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-muted p-3">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => handleBulkAction("suspended")} className="rounded px-3 py-1 text-sm text-[var(--color-warning)] hover:bg-[var(--color-warning)]/5">Suspend All</button>
          <button onClick={() => handleBulkAction("active")} className="rounded px-3 py-1 text-sm text-[var(--color-success)] hover:bg-[var(--color-success)]/5">Activate All</button>
          <button onClick={() => handleBulkAction("archived")} className="rounded px-3 py-1 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/5">Archive All</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-muted-foreground">Clear</button>
        </div>
      )}

      {/* Create form */}
      {orgId ? (
        <TemplateLibrary
          orgId={orgId}
          templates={templates}
          onChanged={() => fetchTemplates(orgId)}
        />
      ) : null}

      {showCreate && (
        <CreateForm
          orgId={orgId}
          templates={templates}
          onCreated={() => {
            setShowCreate(false);
            setSuccess("Client activation started.");
            setError(null);
            fetchAccounts(orgId);
          }}
          onError={() => setError("Failed to activate the client.")}
          onTemplatesChanged={() => fetchTemplates(orgId)}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Table */}
      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-4 py-3"><input type="checkbox" checked={selected.size === accounts.length && accounts.length > 0} onChange={toggleAll} /></th>
              <th className="px-4 py-3 font-medium">Account</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No sub-accounts yet.</td></tr>
            )}
            {accounts.map((a) => (
              <tr key={a.id} className="stagger-item border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3"><input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} /></td>
                <td className="px-4 py-3">
                  <p className="font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.slug}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    a.status === "active" ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
                    a.status === "suspended" ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                  }`}>{a.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(a.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {(STATUS_ACTIONS[a.status] ?? []).map((action) => (
                      <button key={action.status} onClick={() => handleStatusChange(a.id, action.status)} className={`rounded px-2 py-1 text-xs hover:bg-accent ${action.color}`}>
                        {action.label}
                      </button>
                    ))}
                    <a href={`/admin/tenants?id=${a.id}`} className="rounded px-2 py-1 text-xs text-primary hover:bg-primary/10">Enter</a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateLibrary({ orgId, templates, onChanged }: { orgId: string; templates: AgencyTemplate[]; onChanged: () => void | Promise<void> }) {
  const [name, setName] = useState("");
  const [vertical, setVertical] = useState("agency");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPushing, setIsPushing] = useState<string | null>(null);

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await apiFetch(`/platform/agency/templates/${orgId}`, {
        method: "POST",
        body: JSON.stringify({ name, vertical }),
      });
      setName("");
          await onChanged();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePushTemplate = async (templateId: string) => {
    setIsPushing(templateId);
    try {
      await apiFetch(`/platform/agency/templates/${orgId}/${templateId}/push`, {
        method: "POST",
      });
    } finally {
      setIsPushing(null);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-medium">Agency template library</h2>
          <p className="text-xs text-muted-foreground">Create reusable client blueprints and push linked updates without rebuilding each tenant by hand.</p>
        </div>
      </div>

      <form onSubmit={handleCreateTemplate} className="grid gap-3 md:grid-cols-[1.4fr_1fr_auto]">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
          className="rounded-md border border-input px-3 py-2 text-sm"
        />
        <select value={vertical} onChange={(e) => setVertical(e.target.value)} className="rounded-md border border-input px-3 py-2 text-sm">
          <option value="agency">Agency</option>
          <option value="real_estate">Real Estate</option>
          <option value="dental">Dental</option>
          <option value="salon">Salon</option>
          <option value="trades">Trades</option>
          <option value="gym">Gym</option>
          <option value="saas">SaaS</option>
          <option value="generic">Generic</option>
        </select>
        <button type="submit" disabled={isSubmitting} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {isSubmitting ? "Saving..." : "Create template"}
        </button>
      </form>

      <div className="mt-4 space-y-2">
        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">No agency templates yet. Create one before you activate linked client tenants.</p>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="stagger-item flex items-center justify-between rounded-lg border border-border px-3 py-3">
              <div>
                <p className="font-medium">{template.name}</p>
                <p className="text-xs text-muted-foreground">{template.vertical.replace(/_/g, " ")} template</p>
              </div>
              <button
                type="button"
                onClick={() => handlePushTemplate(template.id)}
                className="rounded px-3 py-1 text-xs text-primary hover:bg-primary/10"
              >
                {isPushing === template.id ? "Pushing..." : "Push linked updates"}
              </button>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function CreateForm({
  orgId,
  templates,
  onCreated,
  onError,
  onTemplatesChanged,
  onCancel,
}: {
  orgId: string;
  templates: AgencyTemplate[];
  onCreated: () => void;
  onError: () => void;
  onTemplatesChanged: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [industry, setIndustry] = useState("agency");
  const [templateId, setTemplateId] = useState("");
  const [inheritanceMode, setInheritanceMode] = useState<"linked" | "detached">("detached");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !contactEmail) return;
    setIsSubmitting(true);
    try {
      if (inheritanceMode === "linked" && !templateId) {
        setIsSubmitting(false);
        return;
      }

      await apiFetch("/platform/agency/clients/activate", {
        method: "POST",
        body: JSON.stringify({
          orgId,
          businessName: name,
          contactEmail,
          industry,
          timezone: "UTC",
          templateId: templateId || null,
          inheritanceMode,
          loadSampleData: true,
        }),
      });
      onCreated();
    } catch {
      onError();
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-6 rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 font-medium">Create Sub-Account</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Business Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-muted-foreground">The mounted activation flow derives the tenant slug from the business name.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Owner Email</label>
          <input required type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Vertical</label>
          <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm">
            <option value="agency">Agency</option>
            <option value="real_estate">Real Estate</option>
            <option value="dental">Dental</option>
            <option value="salon">Salon</option>
            <option value="trades">Trades</option>
            <option value="gym">Gym</option>
            <option value="saas">SaaS</option>
            <option value="generic">Generic</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Inheritance Mode</label>
          <select value={inheritanceMode} onChange={(e) => setInheritanceMode(e.target.value as "linked" | "detached")} className="w-full rounded-md border border-input px-3 py-2 text-sm">
            <option value="detached">Detached copy</option>
            <option value="linked">Linked to agency template</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Agency Template</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm">
            <option value="">No template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
          {inheritanceMode === "linked" && templates.length === 0 ? (
            <button
              type="button"
              onClick={async () => {
                await apiFetch(`/platform/agency/templates/${orgId}`, {
                  method: "POST",
                  body: JSON.stringify({ name: `${name || "Agency"} Master Template`, vertical: industry }),
                });
                await onTemplatesChanged();
              }}
              className="mt-2 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
            >
              Create template from this vertical
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={isSubmitting} className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">{isSubmitting ? "Creating..." : "Create"}</button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}
